import React, { useEffect, useState } from "react";
import { IoCloseOutline } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import {
  closeModal,
  closeModalWithUpdate,
} from "../../../features/modal/ModalSlice";
import CustomInputBox from "../../Form/CustomInputBox";
import { Numbers, Pause, Phone, PlayArrow } from "@mui/icons-material";
import {
  Box,
  IconButton,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tooltip,
} from "@mui/material";
import CustomButton from "../../Button/CustomButton";
import apiClient from "../../../services/apiClient";
import { LuTextCursorInput } from "react-icons/lu";
import { FaUserTie } from "react-icons/fa6";
import { BsFillQuestionCircleFill } from "react-icons/bs";
import { AiOutlineCode } from "react-icons/ai";

const EditAIAgent = () => {
  const [formData, setFormData] = useState({
    agent_name: "",
    assigned_extension: "",
    assigned_number: "",
    request_status: "",
    personality: "",
    sales_script: "",
    assigned_voice_type: "",
  });
  const [voiceData, setVoiceData] = useState([]);
  const [playingId, setPlayingId] = useState(null);
  const [audio, setAudio] = useState(null);
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state?.userInfo?.user);
  const modal = useSelector((state) => state?.modal);
  const agentId = modal?.modal?.data?._id;
  const [extensions, setExtensions] = useState([]);
    const [loading, setLoading] = useState(false);

  // Fetch details from API instead of Redux dispatch
  useEffect(() => {
    const fetchAgentDetails = async () => {
      try {
        const response = await apiClient.get(`/agent-details-id/${agentId}`);
        const data = response.data;
        // console.log(data, "datatatattata");

        setFormData({
          agent_name: data?.agent_name || "",
          assigned_extension: data?.assigned_extension || "",
          assigned_number: data?.assigned_number || "",
          sales_script: data?.sales_script || "",
          assigned_voice_type: data?.assigned_voice_type?.id || "",
          personality: data?.personality,
        });
      } catch (error) {
        console.error("Failed to fetch agent details:", error);
      }
    };

    if (agentId) fetchAgentDetails();
  }, [agentId]);

  const handleClosePopup = () => {
    dispatch(closeModal());
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      user_id: userInfo?.id,
      company_id: userInfo?.company_id,
    };
   setLoading(true);
    try {
      await apiClient.put(`/update-agent-details/${agentId}`, payload);

      dispatch(
        closeModalWithUpdate({
          isUpdated: {
            isUpdatedIn: "UPDATE_REQUEST",
            data: null,
          },
        })
      );
    } catch (error) {
      console.error("Error saving request:", error);
    }
    finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVoice();
    fetchExtensions();
  }, []);

  const fetchVoice = async () => {
    try {
      const response = await apiClient.get("/agent-voice");
      const data = response?.data || [];
      // console.log(data);
      setVoiceData(data);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchExtensions = async () => {
    try {
      const response = await apiClient.get(
        `/assignedPhoneDetails?owner_id=${userInfo?.id}&company_id=${userInfo?.company_id}&assigned_phone_status=not-assigned&page=1&page_size=100`
      );
      setExtensions(response?.data || []);
    } catch (error) {
      console.error("Error fetching extensions:", error);
    }
  };

  const handlePlay = (ele) => {
    if (audio) {
      audio.pause();
      setAudio(null);
      setPlayingId(null);
    }

    if (playingId !== ele._id) {
      const newAudio = new Audio(
        `https://dialgrid-api.greymatterz.com/${ele.sample_voice.replace(/\\/g, "/")}`
      );
      newAudio.play();
      setAudio(newAudio);
      setPlayingId(ele._id);

      newAudio.onended = () => {
        setPlayingId(null);
        setAudio(null);
      };
    }
  };

  return (
    <div className={"rightModal taskModal"}>
      <div className="modal-header p-3">
        <h4 className="m-0">Edit Agent Details</h4>
        <button className="btnClose ms-auto" onClick={handleClosePopup}>
          <IoCloseOutline />
        </button>
      </div>

      <div className="p-4">
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <div className="inputData withIcon mb-4">
              <span className="icon">
                <LuTextCursorInput />
              </span>
              <input
                className="form-control"
                type="text"
                name="agent_name"
                required
                value={formData?.agent_name}
                onChange={handleChange}
              />
              <label className="label">Agent Name</label>
            </div>

            <div className="inputData selectForm mb-4">
              <span className="icon"></span>

              <FormControl fullWidth>
                <InputLabel>Choose AI Voice</InputLabel>
                <Select
                  name="assigned_voice_type"
                  value={formData.assigned_voice_type || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      assigned_voice_type: e.target.value,
                    }))
                  }
                  renderValue={(selected) => {
                    const voice = voiceData.find((v) => v._id === selected);
                    return voice ? voice.voice_name : "Choose AI Voice";
                  }}
                  sx={{
                    "& .MuiSelect-select": {
                      padding: "12.5px 46px",
                    },
                  }}
                >
                  {voiceData.map((ele) => (
                    <MenuItem key={ele._id} value={ele._id}>
                      <ListItemText primary={ele.voice_name} />
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePlay(ele);
                        }}
                      >
                        {playingId === ele._id ? <Pause /> : <PlayArrow />}
                      </IconButton>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="inputData selectForm mb-4">
              <span className="icon"></span>

              <FormControl fullWidth>
                <InputLabel>Choose Extension</InputLabel>
                <Select
                  name="assigned_extension"
                  value={formData?.assigned_extension || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      assigned_extension: e.target.value,
                    }))
                  }
                  sx={{
                    "& .MuiSelect-select": {
                      padding: "12.5px 46px",
                    },
                  }}
                >
                  {extensions?.map((ext, index) => (
                    <MenuItem key={index} value={ext?.data?.username}>
                      {ext?.data?.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className="inputData selectForm mb-4">
              <span className="icon"></span>

              <FormControl fullWidth>
                <InputLabel>Choose Number</InputLabel>
                <Select
                  name="assigned_number"
                  value={formData?.assigned_number || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      assigned_number: e.target.value,
                    }))
                  }
                  sx={{
                    "& .MuiSelect-select": {
                      padding: "12.5px 46px",
                    },
                  }}
                >
                  {extensions?.map((ext, index) => (
                    <MenuItem key={index} value={ext?.data?.effective_caller_id_number}>
                      {ext?.data?.effective_caller_id_number}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div class="inputData textareaData withIcon mb-4">
              <span className="icon">
                <FaUserTie />
              </span>
              <textarea
                className="form-control h-auto"
                rows="5"
                cols="80"
                required
                name="personality"
                value={formData.personality}
                onChange={handleChange}
              ></textarea>
              <label className="label">Personality Define</label>
              <div class="needHelp text-end">
                Need Help{" "}
                <Tooltip
                  disableFocusListener
                  title="This is for creating the ai agent for dialgrid. It can only be created in dialgrid from admin."
                >
                  <span className="ms-1">
                    <BsFillQuestionCircleFill />
                  </span>
                </Tooltip>
              </div>
            </div>

            <div class="inputData textareaData withIcon mb-4">
              <span className="icon">
                <AiOutlineCode />
              </span>
              <textarea
                className="form-control h-auto"
                rows="5"
                cols="80"
                required
                name="sales_script"
                value={formData.sales_script}
                onChange={handleChange}
              ></textarea>
              <label className="label">Sales Script</label>
              <div class="needHelp text-end">
                Need Help{" "}
                <Tooltip
                  disableFocusListener
                  title="This is for creating the ai agent for dialgrid. It can only be created in dialgrid from admin."
                >
                  <span className="ms-1">
                    <BsFillQuestionCircleFill />
                  </span>
                </Tooltip>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-3 mb-4">
              <CustomButton
                variant="contained"
                color="secondary"
                onClick={handleClosePopup}
              >
                Cancel
              </CustomButton>
              <CustomButton type="submit" variant="contained" color="primary" loading={loading}>
                Update
              </CustomButton>
            </div>
          </Stack>
        </Box>
      </div>
    </div>
  );
};

export default EditAIAgent;
